<template>
  <div id="app">
    <div class="background-grid"></div>
    <header :class="{ 'scrolled': isScrolled }">
      <nav class="container">
        <div class="logo">
          <span class="company-name">Problem Solver <span class="highlight">Software</span></span>
        </div>
        <ul class="nav-links">
          <li><a href="#about">Mission</a></li>
          <li><a href="#products">Solutions</a></li>
          <li><a href="#contact">Contact</a></li>
        </ul>
      </nav>
    </header>

    <main>
      <section id="hero">
        <div class="hero-content">
          <img src="/logo.png" alt="Problem Solver Software Logo" class="hero-logo" />
          <h1 class="glitch" data-text="SOLVING THE UNSOLVABLE">SOLVING THE UNSOLVABLE</h1>
          <p class="subtitle">Software Engineering from Portland, Oregon.</p>
          <p class="tagline">Building targeted solutions to solve your problems.</p>
          <div class="hero-actions">
            <a href="#products" class="btn primary-btn">Explore Solutions</a>
            <a href="https://marklevy.info" target="_blank" class="btn secondary-btn">Meet The Architect</a>
          </div>
        </div>
      </section>

      <section id="about" class="container">
        <div class="section-header">
          <h2><span class="slash">//</span> OUR MISSION</h2>
        </div>
        <div class="about-card">
          <p>
            Problem Solver Software LLC is not just a dev shop. We are a boutique engineering firm dedicated to 
            <strong>high-performance</strong>, <strong>secure</strong>, and <strong>user-centric</strong> solutions. 
            Founded in 2024 by Mark Levy, we bridge the gap between complex technical challenges and elegant user experiences.
          </p>
        </div>
      </section>

      <section id="products" class="container">
        <div class="section-header">
          <h2><span class="slash">//</span> DEPLOYED SYSTEMS</h2>
        </div>
        
        <div class="products-grid">
          <!-- ParrotPadMD -->
          <article class="product-card parrot">
            <div class="card-glow"></div>
            <div class="card-content">
              <h3>ParrotPadMD</h3>
              <div class="badge">Medical Tech</div>
              <p>The ultimate HIPAA-compliant dictation tool. Works in any EHR, on Mac, Windows, and Chromebook. Powered by Google's Medical AI.</p>
              <ul class="features">
                <li><span class="bullet">›</span> 100% HIPAA Compliant</li>
                <li><span class="bullet">›</span> No IT Integration Required</li>
                <li><span class="bullet">›</span> Specialty-Optimized AI</li>
              </ul>
              <a href="https://parrotpadmd.com" target="_blank" class="btn product-btn">Initialize System <span class="arrow">→</span></a>
            </div>
          </article>

          <!-- Pool Pilot -->
          <article class="product-card pool">
            <div class="card-glow"></div>
            <div class="card-content">
              <h3>Pool Pilot</h3>
              <div class="badge">IoT / Automation</div>
              <p>Smart pool management for the modern era. Automate chemistry logging, get actionable insights, and keep your water crystal clear.</p>
              <ul class="features">
                <li><span class="bullet">›</span> Automated Dosing Calcs</li>
                <li><span class="bullet">›</span> Historical Tracking</li>
                <li><span class="bullet">›</span> Cloud Sync</li>
              </ul>
              <div class="btn-group">
                <a href="https://poolpilot.online" target="_blank" class="btn product-btn">Launch App <span class="arrow">→</span></a>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section id="contact" class="container">
        <div class="glass-panel">
          <h2>Ready to Upgrade?</h2>
          <p>If you have a problem that needs a serious solution, let's talk.</p>
          <a href="mailto:problemsolversoftwarellc@gmail.com" class="btn primary-btn">Execute Contact Protocol</a>
        </div>
      </section>
    </main>

    <footer>
      <div class="container">
        <p>&copy; {{ new Date().getFullYear() }} Problem Solver Software LLC.</p>
        <div class="footer-links">
          <a href="https://parrotpadmd.com" target="_blank">ParrotPadMD</a>
          <span class="separator">|</span>
          <a href="https://poolpilot.online" target="_blank">PoolPilot.online</a>
          <span class="separator">|</span>
          <a href="https://marklevy.info" target="_blank">MarkLevy.info</a>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'App',
  data() {
    return {
      isScrolled: false
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
  },
  unmounted() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 50;
    }
  }
}
</script>

<style>
/* Reset & Base */
:root {
  --neon-cyan: #00f3ff;
  --neon-purple: #bc13fe;
  --bg-dark: #050505;
  --card-bg: rgba(20, 20, 20, 0.6);
  --text-main: #e0e0e0;
  --text-dim: #a0a0a0;
  --font-heading: 'Orbitron', sans-serif;
  --font-body: 'Inter', sans-serif;
}

* { box-sizing: border-box; }

body {
  font-family: var(--font-body);
  background-color: var(--bg-dark);
  color: var(--text-main);
  line-height: 1.6;
  margin: 0;
}

/* Background Animation */
.background-grid {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: -1;
  background-image: 
    linear-gradient(rgba(0, 243, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 243, 255, 0.03) 1px, transparent 1px);
  background-size: 50px 50px;
  perspective: 500px;
  transform-style: preserve-3d;
  animation: bg-scroll 20s linear infinite;
  pointer-events: none;
}

@keyframes bg-scroll {
  0% { transform: translateY(0) rotateX(2deg); }
  100% { transform: translateY(50px) rotateX(2deg); }
}

/* Layout */
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Header */
header {
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 1000;
  padding: 20px 0;
  transition: all 0.3s ease;
}

header.scrolled {
  background: rgba(5, 5, 5, 0.9);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(0, 243, 255, 0.1);
  padding: 10px 0;
}

nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo {
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: 1.2rem;
  color: #fff;
}

.hero-logo {
  width: 280px;
  height: auto;
  margin-bottom: 2rem;
  mix-blend-mode: screen; /* Makes black background transparent */
}

.logo-img {
  height: 50px;
  width: auto;
  mix-blend-mode: screen;
}

.company-name .highlight {
  color: var(--neon-cyan);
}

.circuit-path {
  stroke-dasharray: 200;
  stroke-dashoffset: 200;
  animation: draw 3s ease forwards infinite;
}

.pulse-dot {
  animation: pulse 2s infinite;
}

@keyframes draw {
  0% { stroke-dashoffset: 200; }
  50% { stroke-dashoffset: 0; }
  100% { stroke-dashoffset: -200; }
}

@keyframes pulse {
  0% { opacity: 0.5; r: 5; }
  50% { opacity: 1; r: 7; }
  100% { opacity: 0.5; r: 5; }
}

.nav-links {
  list-style: none;
  display: flex;
  gap: 30px;
}

.nav-links a {
  color: var(--text-main);
  text-decoration: none;
  font-weight: 500;
  text-transform: uppercase;
  font-size: 0.9rem;
  letter-spacing: 1px;
  transition: color 0.3s;
}

.nav-links a:hover {
  color: var(--neon-cyan);
  text-shadow: 0 0 8px var(--neon-cyan);
}

/* Hero */
#hero {
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding-top: 80px;
  position: relative;
  overflow: hidden;
}

#hero::before {
  content: '';
  position: absolute;
  width: 300px;
  height: 300px;
  background: var(--neon-purple);
  filter: blur(150px);
  opacity: 0.2;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: glow-pulse 5s infinite alternate;
}

@keyframes glow-pulse {
  from { opacity: 0.1; transform: translate(-50%, -50%) scale(0.8); }
  to { opacity: 0.3; transform: translate(-50%, -50%) scale(1.2); }
}

#hero h1 {
  font-family: var(--font-heading);
  font-size: 4rem;
  margin-bottom: 0.2em;
  color: #fff;
  text-shadow: 0 0 10px rgba(0, 243, 255, 0.5);
}

.subtitle {
  font-size: 1.5rem;
  color: var(--neon-cyan);
  margin-bottom: 1rem;
  font-weight: 300;
}

.tagline {
  color: var(--text-dim);
  font-size: 1.1rem;
  margin-bottom: 2.5rem;
}

/* Buttons */
.btn {
  display: inline-block;
  padding: 12px 30px;
  border-radius: 4px; /* Hard corners for tech feel */
  text-decoration: none;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.primary-btn {
  background: transparent;
  border: 1px solid var(--neon-cyan);
  color: var(--neon-cyan);
  box-shadow: 0 0 10px rgba(0, 243, 255, 0.2);
}

.primary-btn:hover {
  background: var(--neon-cyan);
  color: #000;
  box-shadow: 0 0 20px rgba(0, 243, 255, 0.6);
}

.secondary-btn {
  border: 1px solid var(--text-dim);
  color: var(--text-dim);
  margin-left: 20px;
}

.secondary-btn:hover {
  border-color: #fff;
  color: #fff;
}

.product-btn {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  backdrop-filter: blur(5px);
}

.product-btn:hover {
  background: var(--neon-purple);
  box-shadow: 0 0 15px rgba(188, 19, 254, 0.5);
}

.arrow { display: inline-block; transition: transform 0.3s; }
.product-btn:hover .arrow { transform: translateX(5px); }

/* Sections */
section { padding: 80px 0; }

.section-header {
  margin-bottom: 50px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
}

h2 {
  font-family: var(--font-heading);
  font-size: 2.5rem;
  color: #fff;
  margin-bottom: 10px;
}

.slash { color: var(--neon-purple); margin-right: 10px; }

/* About */
.about-card {
  background: var(--card-bg);
  padding: 40px;
  border-left: 4px solid var(--neon-purple);
  backdrop-filter: blur(10px);
}

.about-card p {
  font-size: 1.2rem;
  color: #ccc;
  margin: 0;
}

.about-card strong { color: #fff; font-weight: 700; }

/* Products */
.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
}

.product-card {
  background: rgba(30, 30, 30, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  transition: transform 0.3s, box-shadow 0.3s;
}

.product-card:hover {
  transform: translateY(-5px);
  border-color: var(--neon-cyan);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
}

.card-content { padding: 30px; }

h3 {
  font-family: var(--font-heading);
  font-size: 1.8rem;
  margin: 0 0 10px 0;
  color: #fff;
}

.badge {
  display: inline-block;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--neon-cyan);
  border: 1px solid var(--neon-cyan);
  padding: 2px 8px;
  border-radius: 4px;
  margin-bottom: 15px;
}

.product-card p {
  color: #aaa;
  margin-bottom: 20px;
  min-height: 80px;
}

.features {
  list-style: none;
  padding: 0;
  margin-bottom: 30px;
}

.features li {
  margin-bottom: 10px;
  color: #ddd;
}

.bullet { color: var(--neon-purple); font-weight: bold; margin-right: 8px; }

/* Contact & Footer */
.glass-panel {
  background: linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.01) 100%);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.1);
  padding: 60px;
  text-align: center;
  border-radius: 16px;
}

footer {
  padding: 40px 0;
  border-top: 1px solid rgba(255,255,255,0.05);
  color: #666;
  text-align: center;
  font-size: 0.9rem;
}

.footer-links {
  margin-top: 15px;
}

.footer-links a { color: #888; text-decoration: none; transition: color 0.2s; }
.footer-links a:hover { color: var(--neon-cyan); }
.separator { margin: 0 10px; color: #444; }

/* Responsive */
@media (max-width: 768px) {
  #hero h1 { font-size: 2.5rem; }
  .nav-links { display: none; } /* Simplified mobile nav for now */
}
</style>